import * as React from 'react';
import { formatPattern } from 'react-router';

import useRouter from 'js/lib/useRouter';
import { isPoweruser } from 'js/lib/user';

import { useEnableLearnerGroups } from 'bundles/enterprise-admin-hooks/hooks/useEnableLearnerGroups';
import {
  useEnableMessagingV2,
  useEnablePersonalizedReordering,
} from 'bundles/enterprise-admin-hooks/hooks/useFeatures';
import type { SideNavItem, SideNavSubItem } from 'bundles/enterprise-admin-navigation/components/side-nav/base/types';
import { settingsSideNavConfigDefs } from 'bundles/enterprise-admin-navigation/components/side-nav/config/settingsSideNavConfigDefs';
import {
  type AdminSideNavConfigDefs,
  type AdminSideNavItemConfig,
  type AdminSideNavRuntimeContext,
  type AdminSideNavSubItemConfig,
  sideNavConfigDefs,
} from 'bundles/enterprise-admin-navigation/components/side-nav/config/sideNavConfigDefs';
import { useCanUseCourseBuilder } from 'bundles/enterprise-admin-navigation/components/side-nav/useCanUseCourseBuilder';
import { computeSearchCarry } from 'bundles/enterprise-admin-navigation/utils/navConfigUtils';
import { SETTINGS_PATH } from 'bundles/enterprise-admin-navigation/utils/pathConstants';
import {
  useEnterpriseAdminPermissions,
  useIsGroupAdmin,
  useIsMentorAdmin,
  useIsOrgAdmin,
  useIsStaffAdmin,
} from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import useProductFeature from 'bundles/enterprise-admin-sso-settings/hooks/useProductFeature';
import enterpriseAdminUser from 'bundles/enterprise-admin/enterprise-admin-index';
import type { ThirdPartyOrganization as AdminDashboardOrg } from 'bundles/enterprise-admin/types';

const resolveIsActive = (
  formattedHref: string | undefined,
  runtimeContext: AdminSideNavRuntimeContext,
  isActiveOverride?: (runtimeContext: AdminSideNavRuntimeContext, resolvedHref: string) => boolean
): boolean => {
  if (!formattedHref) {
    return false;
  } else if (isActiveOverride) {
    return isActiveOverride(runtimeContext, formattedHref);
  } else {
    return runtimeContext.isActiveHref(formattedHref);
  }
};

const resolveSubItem = (
  child: AdminSideNavSubItemConfig,
  runtimeContext: AdminSideNavRuntimeContext
): SideNavSubItem | null => {
  if (child.shouldShow && !child.shouldShow(runtimeContext)) return null;
  if (child.requiredPermissions && !runtimeContext.hasPermission(child.requiredPermissions)) return null;

  // Format route template with org slug parameter
  const formattedHref = formatPattern(child.href, runtimeContext.router.params);
  const href = `${formattedHref}${runtimeContext.locationSearchString}`;
  const isActive = resolveIsActive(formattedHref, runtimeContext, child.isActiveOverride);

  return { id: child.id, label: child.getLabel(runtimeContext), href, isActive };
};

/**
 * Walks a single `AdminSideNavItemConfig` through the runtime environment.
 *
 * Applies visibility gates (shouldShow, requiredPermissions) and resolves hrefs,
 * active state, and children. Returns null if the item should be dropped (failed
 * gate or permission check, or parent with all children gated out).
 */
const resolveItem = (item: AdminSideNavItemConfig, runtimeContext: AdminSideNavRuntimeContext): SideNavItem | null => {
  if (item.shouldShow && !item.shouldShow(runtimeContext)) return null;
  if (item.requiredPermissions && !runtimeContext.hasPermission(item.requiredPermissions)) return null;

  const children = item.children
    ?.map((child) => resolveSubItem(child, runtimeContext))
    .filter((c): c is SideNavSubItem => c !== null);

  // Parent with declared but no children to shown is dropped — avoids dead expandables.
  if (item.children && item.children.length > 0 && (!children || children.length === 0)) {
    return null;
  }

  // Format route template with org slug parameter
  const rawHref = item.hrefOverride ? item.hrefOverride(runtimeContext) : item.href;
  const formattedHref = rawHref ? formatPattern(rawHref, runtimeContext.router.params) : undefined;
  const href = formattedHref ? `${formattedHref}${runtimeContext.locationSearchString}` : undefined;
  const isActive = resolveIsActive(formattedHref, runtimeContext, item.isActiveOverride);

  return {
    id: item.id,
    label: item.getLabel(runtimeContext),
    icon: item.icon,
    href,
    isActive,
    children,
    showNewBadge: item.showNewBadge ? item.showNewBadge(runtimeContext) : undefined,
  };
};

const resolveItems = (items: AdminSideNavItemConfig[], runtimeContext: AdminSideNavRuntimeContext): SideNavItem[] =>
  items.map((item) => resolveItem(item, runtimeContext)).filter((item): item is SideNavItem => item !== null);

type UseAdminSideNavItemsArgs = {
  thirdPartyOrganization: AdminDashboardOrg;
  enterpriseAdminFeatureFlags: {
    enableSkillsPlanningForOrg: boolean;
    enableNewLearnersPage: boolean;
  };
};

export type AdminSideNavItems = {
  sideNavTopItems: SideNavItem[];
  sideNavBottomItems: SideNavItem[];
  isAdminSettings: boolean;
};

/**
 * Resolves the declarative side-nav config into ready-to-render items.
 *
 * Splits the result into the main `sideNav` list (rendered by `SideNavBar`'s
 * `items` prop) and the `sideNavBottom` list (rendered inside the `bottom` slot).
 *
 * Action items (e.g. Ask Coursera) are NOT returned here — the consumer
 * component appends them onto `sideNav` itself.
 */
export const useSideNavItems = ({
  thirdPartyOrganization,
  enterpriseAdminFeatureFlags,
}: UseAdminSideNavItemsArgs): AdminSideNavItems => {
  const router = useRouter();
  const { pathname, search } = router.location;
  const { hasPermission, getIsOrganizationDomainAdmin } = useEnterpriseAdminPermissions();
  const { enableSkillsPlanningForOrg, enableNewLearnersPage } = enterpriseAdminFeatureFlags;
  const canUseCourseBuilder = useCanUseCourseBuilder();
  const enableMessagingV2 = useEnableMessagingV2(thirdPartyOrganization.id);
  const enableLearnerGroups = useEnableLearnerGroups(thirdPartyOrganization.id);
  const enablePersonalizedReordering = useEnablePersonalizedReordering(thirdPartyOrganization.id);
  const { isUnifiedSsoEnabled } = useProductFeature();
  const isOrgAdmin = useIsOrgAdmin();
  const isGroupAdmin = useIsGroupAdmin();
  const isStaffAdmin = useIsStaffAdmin();
  const isMentorAdmin = useIsMentorAdmin();
  const isPowerUser = isPoweruser();

  const isSuperUserOrEnterpriseOutsourcing = enterpriseAdminUser.isSuperUserOrEnterpriseOutsourcing();
  const isFreemiumOrgAndCanOnlyManagePrograms =
    enterpriseAdminUser.isFreemiumOrg && enterpriseAdminUser.canOnlyManagePrograms();

  const locationSearchString = React.useMemo(() => computeSearchCarry(search || ''), [search]);
  const isAdminSettings = router.isActive(formatPattern(SETTINGS_PATH, router.params));

  // `router` is replaced on every navigation by the router context provider, so the
  // memo recomputes — fresh isActive flags on every URL change without an extra dep.
  // `pathname` is also listed because the client router can mutate location in place.
  return React.useMemo(() => {
    const runtime: AdminSideNavRuntimeContext = {
      router,
      thirdPartyOrganization,
      enableSkillsPlanningForOrg,
      enableNewLearnersPage,
      enableMessagingV2,
      enableLearnerGroups,
      hasPermission,
      isOrganizationDomainAdmin: getIsOrganizationDomainAdmin(),
      isOrgAdmin,
      isGroupAdmin,
      isStaffAdmin,
      isMentorAdmin,
      isPowerUser,
      canUseCourseBuilder,
      isSuperUserOrEnterpriseOutsourcing,
      isFreemiumOrgAndCanOnlyManagePrograms,
      locationSearchString,
      isUnifiedSsoEnabled,
      enablePersonalizedReordering,
      isActiveHref: isAdminSettings
        ? // Exact match required for admin settings left side nav
          (href) => router.isActive(href, true)
        : router.isActive,
    };

    const configDefs: AdminSideNavConfigDefs = isAdminSettings ? settingsSideNavConfigDefs : sideNavConfigDefs;

    return {
      sideNavTopItems: resolveItems(configDefs.top, runtime),
      sideNavBottomItems: resolveItems(configDefs.bottom, runtime),
      isAdminSettings,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pathname forces recompute when the client router mutates location in place
  }, [
    isAdminSettings,
    router,
    pathname,
    thirdPartyOrganization,
    enableSkillsPlanningForOrg,
    enableNewLearnersPage,
    enableMessagingV2,
    enableLearnerGroups,
    hasPermission,
    getIsOrganizationDomainAdmin,
    isOrgAdmin,
    isGroupAdmin,
    isStaffAdmin,
    isMentorAdmin,
    isPowerUser,
    canUseCourseBuilder,
    isSuperUserOrEnterpriseOutsourcing,
    isFreemiumOrgAndCanOnlyManagePrograms,
    locationSearchString,
    isUnifiedSsoEnabled,
    enablePersonalizedReordering,
  ]);
};
